import React, { useEffect } from 'react';
import { TotalsData } from '@/pages/hettwer/types/OfferDetailsData';
import { PositionData } from '@/pages/hettwer/types/PositionData';

interface TotalsBlockProps {
  totals: TotalsData;
  positions: PositionData[];
  isDraft: boolean;
  onChange: (totals: TotalsData) => void;
}

/* ==================================================
   Helper
   ================================================== */

const round2 = (v: number) => Math.round(v * 100) / 100;

/* ==================================================
   Component
   ================================================== */

export const TotalsBlock: React.FC<TotalsBlockProps> = ({
  totals,
  positions,
  isDraft,
  onChange
}) => {
  /* ==================================================
     Automatic calculation (ONLY draft)
     ================================================== */

  useEffect(() => {
    if (!isDraft) return;

    const net = round2(positions.reduce((sum, p) => sum + (p.pricing?.total_price ?? 0), 0));

    const vatRate = totals.vat_rate ?? 19;
    const vat = round2(net * (vatRate / 100));
    const gross = round2(net + vat);

    // Nur updaten, wenn sich wirklich etwas geändert hat
    if (net !== totals.net_total || vat !== totals.vat_total || gross !== totals.gross_total) {
      onChange({
        ...totals,
        net_total: net,
        vat_total: vat,
        gross_total: gross
      });
    }
  }, [positions, totals.vat_rate, isDraft]);

  /* ==================================================
     Manual edit helpers
     ================================================== */

  const update = (patch: Partial<TotalsData>) => onChange({ ...totals, ...patch });

  const renderInput = (
    label: string,
    value: number | null | undefined,
    onChangeValue: (value: number | null) => void,
    options?: { suffix?: string; readOnly?: boolean }
  ) => (
    <div className="flex flex-col gap-1">
      <label className="text-xs text-gray-600">{label}</label>
      <div className="relative">
        <input
          type="number"
          readOnly={options?.readOnly}
          className={`input input-sm w-full ${
            options?.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''
          } ${options?.suffix ? 'pr-8' : ''}`}
          value={value ?? ''}
          onChange={(e) => onChangeValue(e.target.value === '' ? null : Number(e.target.value))}
        />
        {options?.suffix && (
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-xs text-gray-500">
            {options.suffix}
          </span>
        )}
      </div>
    </div>
  );

  /* ==================================================
     Render
     ================================================== */

  return (
    <div className="card">
      <div className="card-header">
        <h3 className="card-title">Summen</h3>
      </div>

      <div className="card-body grid grid-cols-1 md:grid-cols-2 gap-5">
        {renderInput('Netto', totals.net_total, () => {}, {
          suffix: '€',
          readOnly: true
        })}

        {renderInput(
          'MwSt-Satz',
          totals.vat_rate,
          (v) => update({ vat_rate: v ?? totals.vat_rate }),
          { suffix: '%' }
        )}

        {renderInput('MwSt-Betrag', totals.vat_total, () => {}, {
          suffix: '€',
          readOnly: true
        })}

        {renderInput('Brutto', totals.gross_total, () => {}, {
          suffix: '€',
          readOnly: true
        })}
      </div>

      {!isDraft && (
        <div className="px-6 pb-4 text-xs text-gray-500 italic">
          Finales Angebot – Summen werden nicht mehr automatisch berechnet.
        </div>
      )}
    </div>
  );
};
